import axios from "axios";
import qs from "qs";
import { Message, MessageSchema, UpsertMessage } from "../types/Message";
import { toCamelCase, toSnakeCase } from "../utils/objectHelper";
import { omit } from "lodash";

export const MessageTransport = {
    spam: {
        list: async (params?: object): Promise<Message[]> => {
            const { data } = await axios.get<Message[]>(
                `/message/spam/list${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data.map((current) =>
                MessageSchema.cast(toCamelCase(current), { stripUnknown: true })
            );
        },

        listTotal: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/spam/findTotal${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        listTotalFiltered: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/spam/findTotalFiltered${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        columnsValues: async (params?: any) => {
            const { data } = await axios.get(
                `/message/spam/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data[0];
        },
    },

    inProcess: {
        list: async (params?: object): Promise<Message[]> => {
            const { data } = await axios.get<Message[]>(
                `/message/inProcess/list${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data.map((current) =>
                MessageSchema.cast(toCamelCase(current), { stripUnknown: true })
            );
        },

        listTotal: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/inProcess/findTotal${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        listTotalFiltered: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/inProcess/findTotalFiltered${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        columnsValues: async (params?: any) => {
            const { data } = await axios.get(
                `/message/inProcess/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data[0];
        },
    },

    unprocess: {
        list: async (params?: object): Promise<Message[]> => {
            const { data } = await axios.get<Message[]>(
                `/message/unprocess/list${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data.map((current) =>
                MessageSchema.cast(toCamelCase(current), { stripUnknown: true })
            );
        },

        listTotal: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/unprocess/findTotal${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        listTotalFiltered: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/unprocess/findTotalFiltered${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        columnsValues: async (params?: any) => {
            const { data } = await axios.get(
                `/message/unprocess/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data[0];
        },
    },

    paid: {
        list: async (params?: object): Promise<Message[]> => {
            const { data } = await axios.get<Message[]>(
                `/message/paid/list${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data.map((current) =>
                MessageSchema.cast(toCamelCase(current), { stripUnknown: true })
            );
        },

        listTotal: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/paid/findTotal${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        listTotalFiltered: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/paid/findTotalFiltered${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        columnsValues: async (params?: any) => {
            const { data } = await axios.get(
                `/message/paid/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data[0];
        },
    },
    published: {
        list: async (params?: object): Promise<Message[]> => {
            const { data } = await axios.get<Message[]>(
                `/message/published/list${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data.map((current) =>
                MessageSchema.cast(toCamelCase(current), { stripUnknown: true })
            );
        },

        listTotal: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/published/findTotal${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        listTotalFiltered: async (params?: object): Promise<number> => {
            const { data } = await axios.get<number>(
                `/message/published/findTotalFiltered${params ? `?${qs.stringify(params)}` : ""}`
            );

            return data;
        },

        columnsValues: async (params?: any) => {
            const { data } = await axios.get(
                `/message/published/columnsValues${params ? `?${qs.stringify(params)}` : ""}`,
                {}
            );
            return data[0];
        },
    },

    save: async (object: UpsertMessage, id: number | undefined | null): Promise<Message> => {
        const response = await axios.request<Message>({
            method: id ? "put" : "post",
            url: id ? `/ips/${id}` : "/ips",
            data: toSnakeCase(object),
        });

        return MessageSchema.cast(toCamelCase(response.data), { stripUnknown: true });
    },

    saveBulk: async (objects: Array<UpsertMessage>): Promise<Array<Message>> => {
        const { data } = await axios.request<Array<Message>>({
            method: "put",
            url: "message/bulkSave",
            data: toSnakeCase({ messages: { ...objects } }),
        });

        return data.map((current) =>
            MessageSchema.cast(toCamelCase(current), { stripUnknown: true })
        );
    },

    toggleActive: async (message: Message): Promise<void> => {
        const action = "toggleActive";
        await axios.patch<Message>(`/message/${message.id}/actions/${action}`);
    },

    getprivateEmailFiles: async (filename: string): Promise<string | null> => {
        const { data } = await axios.get<string>(`/s3/privateEmailFiles?filename=${filename}`);
        return data;
    },

    sendEmail: async (object: UpsertMessage, messageId: number): Promise<Message> => {
        const { data } = await axios.request<Message>({
            method: "put",
            url: `message/${messageId}/send`,
            data: toSnakeCase(object),
        });

        return MessageSchema.cast(toCamelCase(data), { stripUnknown: true });
    },

    formTransformer: (object: Message): UpsertMessage | null => {
        if (!object) {
            return null;
        }
        return omit(
            {
                ...object,
                moneySiteId: object.moneySite.id,
            },
            "moneySite"
        );
    },
};
